import { FC } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { ITimelineMetric } from '../../../../models/injury';
import { ChartEmptyState } from './_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: ITimelineMetric | null;
  period?: string;
  height?: number;
}

export const InjuryTimelineOverlay: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  const gradientId = 'timelineGradient';

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <AreaChart data={data.series} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--c-text-0)" stopOpacity={0.25} />
            <stop offset="95%" stopColor="var(--c-text-0)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="date" tickFormatter={(v) => dayjs(v).format('DD/MM')} tick={chartAxisTick} />
        <YAxis hide />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(value) => [value, 'Valore']}
          labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
        />
        <Area type="monotone" dataKey="value" stroke="var(--c-text-0)" strokeWidth={2} fill={`url(#${gradientId})`} />
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine
            key={marker.injuryId}
            x={marker.date}
            stroke="var(--ri-critical)"
            strokeWidth={1.5}
            label={{
              value: marker.bodyZone,
              position: 'top',
              fontSize: 10,
              fill: 'var(--ri-critical)',
              fontFamily: "'Satoshi', sans-serif",
            }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};
